import { filterNullable } from "@-/com.npmjs.array-utils-ts";
import { eq } from "@-/com.npmjs.drizzle-orm";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { getMorphemeDetailsTileSlices } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useClearActorDeck } from "@-/dev.improve.haniverse.lib.cards.deck.clear";
import {
  ensureUserCardRows,
  startUserCardSchedule,
} from "@-/dev.improve.haniverse.lib.cards.user-card.rows";

// A small, known set of cards used to seed the deck for testing answer mode.
export const PRESET_DECK_CHARACTERS = ["一", "二", "三", "人", "口"] as const;

// Resets the actor deck to a fixed set of cards (`PRESET_DECK_CHARACTERS`) so
// answer mode can be exercised against a deterministic deck. Wipes existing
// review state first via `useClearActorDeck`, then seeds fresh FSRS/user cards.
export function usePresetActorDeck() {
  const clearActorDeckMutation = useClearActorDeck();
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);

  return useMutation({
    mutationFn: async (): Promise<number> => {
      // Wipe existing review state
      await clearActorDeckMutation.mutateAsync();

      const presetCards = await getMorphemeDetailsTileSlices(
        contentDb,
        staticDb,
        PRESET_DECK_CHARACTERS.map((morpheme) => ({ morpheme })),
      );
      const cardsByFront = new Map(filterNullable(presetCards).map((c) => [c.front, c] as const));

      const nowIso = new Date().toISOString();
      let seededCount = 0;

      for (const character of PRESET_DECK_CHARACTERS) {
        const card = cardsByFront.get(character);
        if (card === undefined) {
          continue;
        }

        await actorDb(import.meta.location).transaction(async (tx) => {
          const { ids } = await ensureUserCardRows(
            tx,
            { grapheme: character, kind: "meaning" },
            nowIso,
          );
          await startUserCardSchedule(tx, {
            dueIso: nowIso,
            fsrsCardId: ids.fsrsCardId,
            nowIso,
            scheduledDays: 0,
          });
          await tx
            .update(actorSchema.userCard)
            .set({ fsrsCardId: ids.fsrsCardId, startedStudyingAt: nowIso })
            .where(eq(actorSchema.userCard.id, ids.userCardId));
        });

        seededCount += 1;
      }

      return seededCount;
    },
    mutationKey: ["presetActorDeck"],
  });
}
